import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Ban, Pencil } from 'lucide-react';
import { formatDate } from './utils';

/**
 * Dual-mode dialog for creating a coaching / general block AND editing an
 * existing block (including recurring-series scope). Parent owns all state.
 */
export const BlockLaneModal = ({
  open,
  onOpenChange,
  form,
  setForm,
  lanes,
  classes,
  coaches,
  editingBlockId,
  editingBlockHasSeries,
  editScope,
  setEditScope,
  onSubmit,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="block-modal">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {editingBlockId ? (
            <>
              <Pencil className="h-5 w-5 text-slate-600" />
              Edit Block
              {editingBlockHasSeries && (
                <Badge variant="outline" className="ml-2 text-[10px] bg-purple-50 text-purple-700 border-purple-200">
                  Recurring series
                </Badge>
              )}
            </>
          ) : (
            <>
              <Ban className="h-5 w-5 text-red-500" />
              Block Lane
            </>
          )}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-4">
        {/* Block Type */}
        <div className="space-y-2">
          <Label>Block Type</Label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="blockType"
                value="general"
                checked={form.block_type === 'general'}
                onChange={() => setForm({ ...form, block_type: 'general', class_id: '', coach_id: '' })}
                className="text-red-500"
              />
              <span className="text-sm">General Block</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="blockType"
                value="coaching"
                checked={form.block_type === 'coaching'}
                onChange={() => setForm({ ...form, block_type: 'coaching' })}
                className="text-green-500"
              />
              <span className="text-sm">Coaching Session</span>
            </label>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Lane</Label>
          <Select value={form.lane_id} onValueChange={(v) => setForm({ ...form, lane_id: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {lanes.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Date</Label>
          <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Start Time</Label>
            <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>End Time</Label>
            <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
          </div>
        </div>

        {form.block_type === 'coaching' && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg space-y-4">
            <p className="text-sm font-medium text-green-700">Coaching Details</p>
            <div className="space-y-2">
              <Label>Class</Label>
              <Select value={form.class_id || 'none'} onValueChange={(v) => setForm({ ...form, class_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Select class..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (Manual Entry)</SelectItem>
                  {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} - {c.schedule_day}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Coach</Label>
              <Select value={form.coach_id || 'none'} onValueChange={(v) => setForm({ ...form, coach_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Select coach..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">TBD</SelectItem>
                  {coaches.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="pt-2 border-t border-green-200">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_recurring}
                  onChange={(e) => setForm({ ...form, is_recurring: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Create recurring weekly blocks</span>
              </label>
              {form.is_recurring && (
                <div className="mt-3 space-y-2">
                  <Label className="text-sm">Number of weeks</Label>
                  <Select
                    value={String(form.recurrence_weeks)}
                    onValueChange={(v) => setForm({ ...form, recurrence_weeks: parseInt(v) })}
                  >
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4">4 weeks</SelectItem>
                      <SelectItem value="8">8 weeks</SelectItem>
                      <SelectItem value="12">12 weeks</SelectItem>
                      <SelectItem value="16">16 weeks</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        )}

        {form.block_type === 'general' && (
          <div className="space-y-2">
            <Label>Reason</Label>
            <Input
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="e.g., Maintenance, Private Event"
            />
          </div>
        )}

        {editingBlockId && editingBlockHasSeries && (
          <div className="space-y-2 rounded-md bg-purple-50/60 border border-purple-200 px-3 py-2.5" data-testid="edit-scope-picker">
            <Label className="text-sm text-purple-900">This block is part of a recurring series. Apply changes to:</Label>
            <div className="flex flex-col gap-1.5 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="editScope" value="this" checked={editScope === 'this'} onChange={() => setEditScope('this')} data-testid="edit-scope-this" />
                <span>Only this occurrence ({formatDate(form.date)})</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="editScope" value="future" checked={editScope === 'future'} onChange={() => setEditScope('future')} data-testid="edit-scope-future" />
                <span>This and all future occurrences</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="editScope" value="all" checked={editScope === 'all'} onChange={() => setEditScope('all')} data-testid="edit-scope-all" />
                <span>All occurrences in the series</span>
              </label>
            </div>
            {editScope !== 'this' && (
              <p className="text-[11px] text-purple-700 pt-1">
                Note: changing the date only applies to this occurrence; series edits update every linked block's time / lane / class / reason.
              </p>
            )}
          </div>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="block-modal-cancel">Cancel</Button>
        <Button
          onClick={onSubmit}
          className={form.block_type === 'coaching' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}
          data-testid="block-modal-save"
        >
          {editingBlockId
            ? (editingBlockHasSeries
                ? (editScope === 'this' ? 'Save This Occurrence' : editScope === 'future' ? 'Save This & Future' : 'Save Entire Series')
                : 'Save Changes')
            : (form.block_type === 'coaching' ? 'Create Coaching Block' : 'Block Lane')}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default BlockLaneModal;
